export type ProcessingActivitySurface = 'playground' | 'index' | 'workflows' | 'mcp';
export type ProcessingActivityReason = 'sign_in' | 'page_entry' | 'interaction' | 'upload_intent';

export interface ProcessingActivityRequest {
  event_id: string;
  reason: ProcessingActivityReason;
  surface: ProcessingActivitySurface;
  tool?: string;
}

export interface ProcessingActivityResponse {
  accepted: boolean;
  warm_until: string | null;
  wake_status: 'accepted' | 'failed' | 'not_needed';
  reason: 'recorded' | 'duplicate' | 'coalesced' | 'disabled' | 'unavailable';
}

export interface InteractiveLoginEvent {
  userId: string;
  sequence: number;
}

export type ProcessingActivityDestination = Pick<ProcessingActivityRequest, 'surface' | 'tool'>;

const PROCESSING_MCP_TOOLS = new Set([
  'analyze', 'import_media',
]);

export const getProcessingActivityDestination = (
  pathname: string,
  search: string,
  mcpEnabled = true,
): ProcessingActivityDestination | null => {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/playground' || path === '/index' || path === '/workflows') {
    return { surface: path.slice(1) as ProcessingActivitySurface };
  }
  if (path === '/mcp' && mcpEnabled) {
    const tool = new URLSearchParams(search).get('tool') || '';
    if (PROCESSING_MCP_TOOLS.has(tool)) return { surface: 'mcp', tool };
  }
  return null;
};

type UploadIntentListener = (expectedUserId?: string) => void;
const uploadIntentListeners = new Set<UploadIntentListener>();

export const subscribeProcessingUploadIntent = (listener: UploadIntentListener): (() => void) => {
  uploadIntentListeners.add(listener);
  return () => { uploadIntentListeners.delete(listener); };
};

// This advisory notification must never delay or fail the user's upload.
export const notifyProcessingUploadIntent = (expectedUserId?: string): void => {
  uploadIntentListeners.forEach((listener) => {
    try { listener(expectedUserId); } catch { /* The next real action may retry. */ }
  });
};

export const createProcessingActivityEventId = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
